export class ListeningTracker {
    constructor(player, liveItemId) {
        this.player = player;
        this.liveItemId = liveItemId;
        this.historyId = null;
        this.isPlaying = false;
        this.updateInterval = null;
        this.backgroundTime = 0;
        this.backgroundStartTime = null;
        this.wasVisible = !document.hidden;
        this.isCleaningUp = false; // Guard against multiple cleanup calls
        this.isStarting = false;   // Guard against multiple start calls
        this.sessionStartTime = null;

        console.log('ListeningTracker initialized:', {
            player: this.player,
            liveItemId: this.liveItemId,
            isVisible: !document.hidden
        });

        this.setupEventListeners();
        this.setupPageLifecycleHandlers();
    }

    setupEventListeners() {
        // Start tracking when playback starts
        this.player.addEventListener('play', () => {
            console.log('Play event detected');
            this.handlePlay();
        });

        // Stop tracking when playback ends
        this.player.addEventListener('ended', () => {
            console.log('End event detected');
            this.stopSession();
        });

        // Handle pause events
        this.player.addEventListener('pause', () => {
            console.log('Pause event detected');
            this.handlePause();
        });

        // Handle playing events
        this.player.addEventListener('playing', () => {
            console.log('Playing event detected');
            this.handlePlay();
        });
    }

    handlePlay() {
        this.isPlaying = true;
        // If we're already in background when play starts, start tracking
        if (document.hidden) {
            this.backgroundStartTime = Date.now();
        }
        
        // Start a new session if none exists
        if (!this.historyId) {
            this.startSession();
        }

        // Track play event
        if (window.posthog) {
            window.posthog.capture('audio_play', {
                liveItemId: this.liveItemId,
                historyId: this.historyId,
                isBackground: document.hidden
            });
        }
    }

    handlePause() {
        this.isPlaying = false;
        // If we were tracking background time, accumulate it
        if (this.backgroundStartTime) {
            const backgroundDuration = (Date.now() - this.backgroundStartTime) / 1000 / 60;
            this.backgroundTime += backgroundDuration;
            console.log('Accumulated background time on pause:', {
                duration: backgroundDuration,
                total: this.backgroundTime
            });
            this.backgroundStartTime = null;
        }

        // Track pause event
        if (window.posthog) {
            window.posthog.capture('audio_pause', {
                liveItemId: this.liveItemId,
                historyId: this.historyId,
                backgroundTime: this.backgroundTime
            });
        }
    }

    setupPageLifecycleHandlers() {
        // Handle page visibility changes
        document.addEventListener('visibilitychange', () => {
            const isVisible = !document.hidden;
            console.log('Visibility changed:', { 
                isVisible, 
                wasVisible: this.wasVisible,
                isPlaying: this.isPlaying 
            });
            
            if (!isVisible && this.isPlaying) {
                // Starting background playback
                console.log('Page hidden while playing, starting background time tracking');
                this.backgroundStartTime = Date.now();
            } else if (isVisible && this.isPlaying && this.backgroundStartTime) {
                // Coming back to foreground, accumulate background time
                const backgroundDuration = (Date.now() - this.backgroundStartTime) / 1000 / 60;
                this.backgroundTime += backgroundDuration;
                console.log('Accumulated background time:', {
                    duration: backgroundDuration,
                    total: this.backgroundTime
                });
                this.backgroundStartTime = null;
            }
            
            this.wasVisible = isVisible;
        });

        // Create a single cleanup handler to be used for all cleanup events
        const cleanupHandler = () => {
            if (!this.isCleaningUp) {
                console.log('Cleaning up session');
                this.cleanup();
            }
        };

        // Handle page unload
        window.addEventListener('beforeunload', cleanupHandler, { once: true });

        // Handle Livewire navigation
        document.addEventListener('livewire:navigating', cleanupHandler, { once: true });

        // Handle regular navigation events
        if (window.navigation) {
            window.navigation.addEventListener('navigate', cleanupHandler, { once: true });
        }
    }

    async startSession() {
        if (this.historyId || this.isStarting) {
            console.log('Session already started or starting');
            return;
        }

        this.isStarting = true;
        this.sessionStartTime = Date.now();

        try {
            // Try to get CSRF token from meta tag
            let csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
            
            // If no CSRF token in meta tag, try to get it from the cookie
            if (!csrfToken) {
                const cookies = document.cookie.split(';');
                for (let cookie of cookies) {
                    const [name, value] = cookie.trim().split('=');
                    if (name === 'XSRF-TOKEN') {
                        csrfToken = decodeURIComponent(value);
                        break;
                    }
                }
            }

            // If still no CSRF token, try to fetch it
            if (!csrfToken) {
                try {
                    const response = await fetch('/sanctum/csrf-cookie');
                    if (response.ok) {
                        // Try getting the token again after the fetch
                        csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
                        if (!csrfToken) {
                            const cookies = document.cookie.split(';');
                            for (let cookie of cookies) {
                                const [name, value] = cookie.trim().split('=');
                                if (name === 'XSRF-TOKEN') {
                                    csrfToken = decodeURIComponent(value);
                                    break;
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error fetching CSRF token:', error);
                }
            }

            if (!csrfToken) {
                console.error('CSRF token not found after all attempts');
                this.isStarting = false;
                return;
            }

            console.log('Starting session with token');

            const response = await fetch('/listening-history/start', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    liveItemId: this.liveItemId,
                    isVisible: !document.hidden
                }),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Failed to start session: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            this.historyId = data.historyId;
            this.isPlaying = true;
            this.backgroundTime = 0;
            this.backgroundStartTime = document.hidden ? Date.now() : null;
            
            console.log('Started listening session:', {
                historyId: this.historyId,
                isVisible: !document.hidden
            });

            // Track session start
            if (window.posthog) {
                window.posthog.capture('audio_session_start', {
                    liveItemId: this.liveItemId,
                    historyId: this.historyId,
                    isBackground: document.hidden
                });
            }

            // Start periodic updates
            this.startPeriodicUpdates();
        } catch (error) {
            console.error('Error starting session:', error);
            // Track error event
            if (window.posthog) {
                window.posthog.capture('audio_session_error', {
                    liveItemId: this.liveItemId,
                    error: error.message,
                    type: 'start_session'
                });
            }
        } finally {
            this.isStarting = false;
        }
    }

    startPeriodicUpdates() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }

        // Update every 15 seconds
        this.updateInterval = setInterval(() => {
            if (this.isPlaying && this.historyId) {
                this.pingSession();
            }
        }, 15000);
    }

    async pingSession() {
        if (!this.historyId) return;

        try {
            // Get CSRF token using the same method as startSession
            let csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
            if (!csrfToken) {
                const cookies = document.cookie.split(';');
                for (let cookie of cookies) {
                    const [name, value] = cookie.trim().split('=');
                    if (name === 'XSRF-TOKEN') {
                        csrfToken = decodeURIComponent(value);
                        break;
                    }
                }
            }

            if (!csrfToken) return;

            // If currently in background, calculate time up to now
            if (this.backgroundStartTime) {
                const backgroundDuration = (Date.now() - this.backgroundStartTime) / 1000 / 60;
                this.backgroundTime += backgroundDuration;
                this.backgroundStartTime = Date.now();
            }

            const response = await fetch('/listening-history/ping', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    historyId: this.historyId,
                    backgroundTime: this.backgroundTime,
                    isVisible: !document.hidden
                }),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Failed to ping session: ${response.status} ${response.statusText}`);
            }

            // Reset background time after successful ping
            this.backgroundTime = 0;
        } catch (error) {
            console.error('Error pinging session:', error);
        }
    }

    async stopSession() {
        if (!this.historyId) return;

        try {
            // Get CSRF token using the same method as startSession
            let csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
            if (!csrfToken) {
                const cookies = document.cookie.split(';');
                for (let cookie of cookies) {
                    const [name, value] = cookie.trim().split('=');
                    if (name === 'XSRF-TOKEN') {
                        csrfToken = decodeURIComponent(value);
                        break;
                    }
                }
            }

            if (!csrfToken) return;

            // If currently in background, calculate time up to now
            if (this.backgroundStartTime) {
                const backgroundDuration = (Date.now() - this.backgroundStartTime) / 1000 / 60;
                this.backgroundTime += backgroundDuration;
                this.backgroundStartTime = null;
            }

            const response = await fetch('/listening-history/stop', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    historyId: this.historyId,
                    backgroundTime: this.backgroundTime,
                    isVisible: !document.hidden
                }),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`Failed to stop session: ${response.status} ${response.statusText}`);
            }

            // Track session end with duration
            if (window.posthog) {
                const sessionDuration = this.sessionStartTime ? (Date.now() - this.sessionStartTime) / 1000 : 0;
                window.posthog.capture('audio_session_end', {
                    liveItemId: this.liveItemId,
                    historyId: this.historyId,
                    duration: sessionDuration,
                    backgroundTime: this.backgroundTime
                });
            }

            this.cleanup();
        } catch (error) {
            console.error('Error stopping session:', error);
            // Track error event
            if (window.posthog) {
                window.posthog.capture('audio_session_error', {
                    liveItemId: this.liveItemId,
                    historyId: this.historyId,
                    error: error.message,
                    type: 'stop_session'
                });
            }
        }
    }

    cleanup() {
        if (this.isCleaningUp) return;
        this.isCleaningUp = true;

        // Clear any pending updates
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }

        // Reset all state
        this.historyId = null;
        this.isPlaying = false;
        this.backgroundTime = 0;
        this.backgroundStartTime = null;
        this.isCleaningUp = false;
    }
} 